// Tela obrigatória "Crie uma senha segura" (pedido 2026-09-23: admin e
// funcionário acessam a primeira vez e depois são obrigados a trocar a
// senha — maiúscula, caractere especial, número — e informar o celular).
// Renderizada pelo AuthGate (__root.tsx) no lugar do app inteiro enquanto
// gpsDriveUser.mustChangePassword for true; o backend também recusa tudo
// fora de /auth/* nesse estado (403 PASSWORD_CHANGE_REQUIRED). Clientes
// nunca veem esta tela.
import { useState } from "react";
import { Check, Eye, EyeOff, Loader2, LogOut, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/auth-context";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { STRONG_PASSWORD_RULES, isStrongPassword } from "@/lib/password-policy";
import defaultLogoUrl from "@/assets/gps-drive-logo-v3.png";
import { cn } from "@/lib/utils";

export function ForcePasswordChange() {
  const { gpsDriveUser, changeOwnPassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strong = isStrongPassword(next);
  const matches = next.length > 0 && next === confirm;
  const phoneOk = phone.replace(/\D/g, "").length >= 10;
  const canSubmit = current.length > 0 && strong && matches && phoneOk && next !== current && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await changeOwnPassword({ currentPassword: current, newPassword: next, phone: phone.trim() });
    } catch (err) {
      setError(err instanceof GpsDriveApiError || err instanceof Error ? err.message : "Não foi possível trocar a senha");
    } finally {
      setSaving(false);
    }
  };

  const firstName = gpsDriveUser?.name?.trim().split(/\s+/)[0];

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-100 px-4 py-8 dark:bg-slate-950">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex flex-col items-center text-center">
          <img src={defaultLogoUrl} alt="" className="mb-4 h-10 w-auto object-contain" />
          <div className="grid h-12 w-12 place-items-center rounded-full bg-blue-600/10 text-blue-600">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="mt-3 text-xl font-bold text-slate-900 dark:text-slate-100">Crie uma senha segura</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {firstName ? `${firstName}, por` : "Por"} segurança, contas de administrador e funcionário precisam de uma
            senha forte. Troque sua senha para continuar.
          </p>
        </div>

        <div className="mt-6 space-y-4">
          <Field label="Senha atual">
            <Input
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label="Nova senha">
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={show ? "Ocultar senhas" : "Mostrar senhas"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
              {STRONG_PASSWORD_RULES.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.label} className={cn("flex items-center gap-1.5", ok ? "text-emerald-600" : "text-slate-500")}>
                    {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    {r.label}
                  </li>
                );
              })}
            </ul>
          </Field>
          <Field label="Confirmar nova senha">
            <Input
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm.length > 0 && !matches && <p className="mt-1 text-xs text-destructive">As senhas não coincidem.</p>}
            {next.length > 0 && next === current && (
              <p className="mt-1 text-xs text-destructive">A nova senha precisa ser diferente da atual.</p>
            )}
          </Field>
          <Field label="Seu celular">
            <Input
              inputMode="tel"
              autoComplete="tel"
              placeholder="(11) 98765-4321"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {phone.length > 0 && !phoneOk && <p className="mt-1 text-xs text-destructive">Informe DDD + número.</p>}
          </Field>

          {error && <p className="text-sm font-medium text-destructive">{error}</p>}

          <Button type="submit" disabled={!canSubmit} className="h-11 w-full">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar e continuar
          </Button>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
